import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { updateOfferAsync } from "../../../slices/offer";
import { updateOfferCalculationAsync } from "../../../slices/offer-calculation";
import {
  collectOverviewCalculationIds,
  collectOverviewLines,
  isFirmCalculationTemplate,
  pageRootCalculations,
} from "./utils/offer-overview-scope";
import {
  PRESENTATION_FIELD_KEYS,
  defaultPresentationRules,
  itemNodeKey,
  normalizePresentationRules,
} from "./utils/presentation-rules-shape";
import "./offer-workspace.css";

const MODES = ["show_children", "sum_only", "hide"];
const TABS = ["structure", "document", "lines"];
const ACCENT = "#1463C7";

function pushCalcNode(nodes, seen, calc, { isRoot, depth }) {
  if (!calc?.id || isFirmCalculationTemplate(calc)) return;
  const nodeKey = `calc:${calc.id}`;
  if (seen.has(nodeKey)) return;
  seen.add(nodeKey);
  nodes.push({
    node_key: nodeKey,
    label: calc.title || `Kalkyle ${calc.id}`,
    kind: isRoot ? "root_calc" : "nested_calculation",
    depth,
    calcId: calc.id,
    include_product_short_description: Boolean(
      calc.include_product_short_description
    ),
  });

  (calc.items || []).forEach((item) => {
    const nested = item?.nested_calculation;
    if (nested && !isFirmCalculationTemplate(nested)) {
      pushCalcNode(nodes, seen, nested, { isRoot: false, depth: depth + 1 });
    }
  });
}

/**
 * Structure for presentation overrides — overview only (no unused maler).
 */
function structureNodes(offer) {
  const nodes = [];
  const seenCalcs = new Set();

  (offer?.groups || []).forEach((group) => {
    if (!group?.id) return;
    nodes.push({
      node_key: `group:${group.id}`,
      label: group.title || group.name || `Gruppe ${group.id}`,
      kind: "group",
      depth: 0,
    });
  });

  pageRootCalculations(offer?.calculations).forEach((calc) => {
    pushCalcNode(nodes, seenCalcs, calc, { isRoot: true, depth: 0 });
  });

  return nodes;
}

function collectOfferCalcIds(offer) {
  return [...collectOverviewCalculationIds(offer)];
}

function offerHasAnyShortDescription(offer) {
  const walk = (calc) => {
    if (!calc || isFirmCalculationTemplate(calc)) return false;
    if (calc.include_product_short_description) return true;
    return (calc.items || []).some((item) => walk(item?.nested_calculation));
  };
  return pageRootCalculations(offer?.calculations).some(walk);
}

function defaultsKeyForKind(kind) {
  if (kind === "nested_calculation") return "nested_calculation";
  return "group";
}

function FlagRow({ checked, onChange, label, hint, disabled = false }) {
  return (
    <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300"
        style={{ accentColor: ACCENT }}
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium text-slate-800">{label}</span>
        {hint ? (
          <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * Dialog-først presentasjonsregler (ADR-005 / PRES-001).
 * Struktur · dokument (notater/felt) · per linje — workspace holdes ryddig.
 */
export default function OfferPresentationRulesDialog({
  open,
  onClose,
  offer,
  onSaved,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(defaultPresentationRules);
  const [includeShortDescription, setIncludeShortDescription] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("structure");

  useEffect(() => {
    if (!open) return;
    const nextDraft = normalizePresentationRules(offer?.presentation_rules);
    setDraft(nextDraft);
    setIncludeShortDescription(
      Boolean(nextDraft.defaults.include_product_short_description) ||
        offerHasAnyShortDescription(offer)
    );
    setTab("structure");
  }, [open, offer?.id, offer?.presentation_rules, offer?.calculations]);

  const nodes = useMemo(() => structureNodes(offer), [offer]);
  const calcIds = useMemo(() => collectOfferCalcIds(offer), [offer]);
  const overviewLines = useMemo(() => collectOverviewLines(offer), [offer]);

  const modeFor = (nodeKey, kind) => {
    const hit = (draft.rules || []).find((rule) => rule.node_key === nodeKey);
    if (hit?.mode) return hit.mode;
    const key = defaultsKeyForKind(kind);
    return draft.defaults[key];
  };

  const setDefault = (key, mode) => {
    setDraft((prev) => ({
      ...prev,
      defaults: { ...prev.defaults, [key]: mode },
    }));
  };

  const patchDefaults = (patch) => {
    setDraft((prev) => ({
      ...prev,
      defaults: { ...prev.defaults, ...patch },
    }));
  };

  const upsertRule = (nodeKey, patch, kind) => {
    setDraft((prev) => {
      const defaultsMode = prev.defaults[defaultsKeyForKind(kind || "group")];
      const existing = (prev.rules || []).find((rule) => rule.node_key === nodeKey);
      const nextRule = {
        node_key: nodeKey,
        mode: existing?.mode || defaultsMode || "show_children",
        include_nested: existing?.include_nested !== false,
        pricing_role: existing?.pricing_role || "included",
        hidden_fields: existing?.hidden_fields || [],
        ...patch,
      };
      const without = (prev.rules || []).filter((rule) => rule.node_key !== nodeKey);
      const isDefaultMode = nextRule.mode === defaultsMode;
      const noHidden = !nextRule.hidden_fields?.length;
      if (isDefaultMode && noHidden && !nextRule.title_override) {
        return { ...prev, rules: without };
      }
      return { ...prev, rules: [...without, nextRule] };
    });
  };

  const setNodeMode = (nodeKey, mode, kind) => {
    upsertRule(nodeKey, { mode }, kind);
  };

  const toggleLineVisible = (nodeKey, visible) => {
    if (!nodeKey) return;
    upsertRule(nodeKey, { mode: visible ? "show_children" : "hide" }, "group");
  };

  const toggleLineField = (nodeKey, field, shown) => {
    if (!nodeKey) return;
    setDraft((prev) => {
      const existing = (prev.rules || []).find((rule) => rule.node_key === nodeKey);
      const hidden = new Set(existing?.hidden_fields || []);
      if (shown) hidden.delete(field);
      else hidden.add(field);
      const nextRule = {
        node_key: nodeKey,
        mode: existing?.mode || "show_children",
        include_nested: existing?.include_nested !== false,
        pricing_role: existing?.pricing_role || "included",
        hidden_fields: [...hidden],
        title_override: existing?.title_override,
      };
      const without = (prev.rules || []).filter((rule) => rule.node_key !== nodeKey);
      const unused =
        nextRule.mode === "show_children" &&
        !nextRule.hidden_fields.length &&
        !nextRule.title_override;
      return {
        ...prev,
        rules: unused ? without : [...without, nextRule],
      };
    });
  };

  const handleSave = async () => {
    if (!offer?.id) return;
    setSaving(true);
    try {
      const rulesPayload = {
        ...draft,
        defaults: {
          ...draft.defaults,
          include_product_short_description: includeShortDescription,
        },
      };
      const result = await dispatch(
        updateOfferAsync({ id: offer.id, presentation_rules: rulesPayload })
      );
      if (updateOfferAsync.rejected.match(result)) throw new Error("save_failed");

      if (calcIds.length) {
        const calcResults = await Promise.all(
          calcIds.map((id) =>
            dispatch(
              updateOfferCalculationAsync({
                id,
                include_product_short_description: includeShortDescription,
              })
            )
          )
        );
        if (
          calcResults.some((r) => updateOfferCalculationAsync.rejected.match(r))
        ) {
          throw new Error("calc_flag_failed");
        }
      }

      toast.success(t("offers.presentation.save_success"));
      onSaved?.(result.payload, {
        include_product_short_description: includeShortDescription,
        calcIds,
        presentation_rules: rulesPayload,
      });
      onClose?.();
    } catch {
      toast.error(t("offers.presentation.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const kindLabel = (kind) => {
    if (kind === "nested_calculation") {
      return t("offers.presentation.kind.nested_calculation");
    }
    if (kind === "root_calc") {
      return t("offers.presentation.kind.root_calc");
    }
    return t("offers.presentation.kind.group");
  };

  const lineLabel = (row) => {
    const item = row.item;
    return (
      item?.title ||
      item?.description ||
      item?.product?.displayTitle ||
      item?.product?.name ||
      t("offers.presentation.line_fallback", { n: row.item?.id || "" })
    );
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="4xl"
      title={t("offers.presentation.dialog_title")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
        <p className="text-sm text-slate-600">{t("offers.presentation.dialog_hint")}</p>
        <p className="text-xs text-slate-500">{t("offers.presentation.overview_only_hint")}</p>

        <div
          className="inline-flex w-fit rounded-[7px] border border-slate-200 bg-slate-50 p-0.5"
          role="tablist"
        >
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`rounded-[6px] px-3 py-1.5 text-xs font-semibold ${
                tab === id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t(`offers.presentation.tab.${id}`)}
            </button>
          ))}
        </div>

        {tab === "structure" ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">
                  {t("offers.presentation.default_group")}
                </span>
                <select
                  className="offer-select"
                  value={draft.defaults.group}
                  onChange={(e) => setDefault("group", e.target.value)}
                >
                  {MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`offers.presentation.mode.${mode}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">
                  {t("offers.presentation.default_nested")}
                </span>
                <select
                  className="offer-select"
                  value={draft.defaults.nested_calculation}
                  onChange={(e) => setDefault("nested_calculation", e.target.value)}
                >
                  {MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`offers.presentation.mode.${mode}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
              {!nodes.length ? (
                <p className="p-4 text-sm text-slate-500">
                  {t("offers.presentation.empty_tree")}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {nodes.map((node) => (
                    <li
                      key={node.node_key}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                      style={{ paddingLeft: 12 + (node.depth || 0) * 14 }}
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-800">
                          {node.label}
                        </div>
                        <div className="text-xs text-slate-400">
                          {kindLabel(node.kind)} · {node.node_key}
                        </div>
                      </div>
                      <select
                        className="offer-select max-w-full sm:w-auto"
                        value={modeFor(node.node_key, node.kind)}
                        onChange={(e) =>
                          setNodeMode(node.node_key, e.target.value, node.kind)
                        }
                      >
                        {MODES.map((mode) => (
                          <option key={mode} value={mode}>
                            {t(`offers.presentation.mode.${mode}`)}
                          </option>
                        ))}
                      </select>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : null}

        {tab === "document" ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-auto">
            <FlagRow
              checked={Boolean(draft.defaults.include_notes)}
              onChange={(checked) => patchDefaults({ include_notes: checked })}
              label={t("offers.presentation.include_notes")}
              hint={t("offers.presentation.include_notes_hint")}
            />
            <FlagRow
              checked={Boolean(draft.defaults.show_product_info)}
              onChange={(checked) => patchDefaults({ show_product_info: checked })}
              label={t("offers.presentation.show_product_info")}
              hint={t("offers.presentation.show_product_info_hint")}
            />
            <FlagRow
              checked={includeShortDescription}
              onChange={setIncludeShortDescription}
              disabled={!calcIds.length}
              label={t("offers.presentation.include_product_short")}
              hint={t("offers.presentation.include_product_short_hint")}
            />

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">
                {t("offers.presentation.fields_heading")}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {PRESENTATION_FIELD_KEYS.map((field) => (
                  <FlagRow
                    key={field}
                    checked={draft.defaults.show_fields?.[field] !== false}
                    onChange={(checked) =>
                      patchDefaults({
                        show_fields: {
                          ...draft.defaults.show_fields,
                          [field]: checked,
                        },
                      })
                    }
                    label={t(`offers.presentation.field.${field}`)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {tab === "lines" ? (
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
            <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
              {t("offers.presentation.lines_hint")}
            </p>
            {!overviewLines.length ? (
              <p className="p-4 text-sm text-slate-500">
                {t("offers.presentation.empty_lines")}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {overviewLines.map((row, index) => {
                  const key =
                    row.nodeKey ||
                    itemNodeKey(row.item) ||
                    `local:${index}`;
                  const visible = modeFor(key, "group") !== "hide";
                  const hidden = new Set(
                    (draft.rules || []).find((rule) => rule.node_key === key)
                      ?.hidden_fields || []
                  );
                  const type = row.item?.item_type || row.item?.type || "product";
                  return (
                    <li
                      key={key}
                      className="px-3 py-2.5"
                      style={{ paddingLeft: 12 + (row.depth || 0) * 14 }}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-800">
                            {lineLabel(row)}
                          </div>
                          <div className="text-xs text-slate-400">{type}</div>
                        </div>
                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300"
                            style={{ accentColor: ACCENT }}
                            checked={visible}
                            disabled={!row.nodeKey}
                            onChange={(e) =>
                              toggleLineVisible(row.nodeKey, e.target.checked)
                            }
                          />
                          {t("offers.presentation.line_visible")}
                        </label>
                      </div>
                      {visible && type !== "text" && row.nodeKey ? (
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                          {PRESENTATION_FIELD_KEYS.map((field) => (
                            <label
                              key={field}
                              className="inline-flex items-center gap-1 text-[11px] text-slate-600"
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-slate-300"
                                style={{ accentColor: ACCENT }}
                                checked={!hidden.has(field)}
                                onChange={(e) =>
                                  toggleLineField(
                                    row.nodeKey,
                                    field,
                                    e.target.checked
                                  )
                                }
                              />
                              {t(`offers.presentation.field.${field}`)}
                            </label>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700"
            onClick={onClose}
            disabled={saving}
          >
            {t("offers.presentation.cancel")}
          </button>
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: ACCENT }}
            onClick={handleSave}
            disabled={saving || !offer?.id}
          >
            {saving ? t("offers.presentation.saving") : t("offers.presentation.save")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
